// MovementTypeModal.tsx
//
// Cadastro de TIPO DE MOVIMENTO de estoque (Suprimentos). É o rótulo que
// classifica uma movimentação futura — "Compra", "Perda", "Transferência entre
// lojas" — junto com duas decisões de negócio: se aquele tipo mexe no saldo e
// se precisa de aprovação antes de valer.
//
// ONDE GRAVA: `POST/PATCH suprimentos/tipos-de-movimento`, a tabela
// `asup."TipoDeMovimento"`. Quem monta a requisição é o `handleSave` da
// Configuração — por isso o payload daqui usa OS NOMES DE COLUNA do agregado
// (`subsidiary`, `nome`, `descricao`, `categoria`, `afetaEstoque`,
// `requerAprovacao`) e não os nomes do InfoToken: o servidor descarta em
// silêncio toda chave fora da lista branca do agregado, e o que não é chave
// conhecida simplesmente não é gravado.
//
// A LISTA, no entanto, ainda é lida do InfoToken (`movementTypeInfo`,
// DynamicTable case 6), coleção que nenhum backend produz. Então o que for
// criado aqui grava no banco e mesmo assim não aparece na tabela até a lista
// passar a ler o REST paginado.
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/_casca";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

interface ModalPadraoProps {
  selectedObject: any;
  handleCloseModal: () => void;
  handleSave: (objectType: string, payload?: any) => any;
  parseInfoValue: (infoValue: string) => any;
  decryptedInfoToken?: any;
}

/**
 * As categorias: o que VAI PARA O BANCO à esquerda, o que a pessoa lê à direita.
 *
 * O valor gravado tem que ser minúsculo e SEM ACENTO. Não é preferência de
 * estilo: `asup."TipoDeMovimento"` tem
 * `CHECK ("categoria" IN ('entrada','saida','transferencia','ajuste'))`.
 * Mandar "Saída" faz o INSERT ser recusado pelo Postgres, e o usuário recebe
 * um erro de banco em vez de um cadastro.
 *
 * "Ajuste" É O ITEM NOVO: o front já o previa, o banco não tinha. As linhas
 * reais de tipo de movimento até aqui eram três (entrada, saída,
 * transferência), gravadas em `subsidiaryInfo` tipo 1 como texto puro. Quem
 * cadastrar "Ajuste" está cadastrando algo que o legado ainda não conhece —
 * daí o selo na tela, para ninguém descobrir isso depois, na conciliação.
 */
const CATEGORIAS = [
  { valor: "entrada", rotulo: "Entrada" },
  { valor: "saida", rotulo: "Saída" },
  { valor: "transferencia", rotulo: "Transferência" },
  { valor: "ajuste", rotulo: "Ajuste (novo)" },
] as const;

const CATEGORIA_NOVA = "ajuste";

/**
 * Traz para a forma aceita pelo CHECK o que veio do legado.
 *
 * As linhas antigas gravavam a categoria com acento e maiúscula ("Saída"), e
 * as três linhas de `subsidiaryInfo` tipo 1 gravavam em inglês ("entry",
 * "exit", "transfer"). Nenhuma dessas formas passa no CHECK do banco.
 */
const normalizarCategoria = (valor: string): string => {
  const semAcento = String(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  const doLegadoEmIngles: Record<string, string> = {
    entry: "entrada",
    exit: "saida",
    transfer: "transferencia",
    adjustment: "ajuste",
  };
  const traduzido = doLegadoEmIngles[semAcento] ?? semAcento;
  return CATEGORIAS.some((categoria) => categoria.valor === traduzido)
    ? traduzido
    : "";
};

const MovementTypeModal: React.FC<ModalPadraoProps> = ({
  selectedObject,
  handleCloseModal,
  handleSave,
  parseInfoValue,
  decryptedInfoToken,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-movementtypemodal",
  });

  // `{}` é truthy: o useModal faz `setSelectedObject(object || {})`, então
  // testar `selectedObject` sozinho diria "é edição" toda vez. Quem responde é
  // o uuid. `tipoDeMovimentoUuid` é a chave do agregado no REST — e é a MESMA
  // que o `handleSave` do shell lê para decidir entre POST e PATCH; se este
  // modal usasse outra, ele diria "Editar" na barra de título enquanto o shell
  // criava uma linha nova. `movementTypeUUID` é o nome que a DynamicTable monta
  // na linha vinda do InfoToken, e `movementTypeUuid` o do token cru.
  const uuidEmEdicao =
    selectedObject?.tipoDeMovimentoUuid ||
    selectedObject?.movementTypeUUID ||
    selectedObject?.movementTypeUuid ||
    "";
  const ehEdicao = Boolean(uuidEmEdicao);
  const tipoDoModal = ehEdicao ? "updateMovementType" : "createMovementType";

  // Os campos têm o nome DA COLUNA do agregado `tipos-de-movimento`. Manter os
  // dois nomes (um no formulário, outro no payload) é o jeito mais barato de
  // um deles ficar para trás numa alteração futura.
  const [form, setForm] = useState({
    subsidiary: "",
    nome: "",
    descricao: "",
    categoria: "",
    afetaEstoque: true,
    requerAprovacao: false,
  });
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);

  // Salvar com sucesso desmonta o modal (quem fecha é o shell). Sem esta trava,
  // o setState do `finally` cairia em componente desmontado.
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  /** As lojas reais: organization -> companies -> subsidiaries. */
  const lojas: any[] = useMemo(() => {
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    return empresas.flatMap((empresa: any) =>
      (empresa?.subsidiaries ?? []).map((filial: any) => ({
        ...filial,
        companyName: empresa?.companyName,
      }))
    );
  }, [decryptedInfoToken]);

  useEffect(() => {
    // A linha que vem da DynamicTable já chega com o JSON aberto em colunas
    // (`initializeTableData` faz o parse). Mas quando o objeto vem cru do
    // token, o conteúdo ainda está em texto em `movementTypeInfoValue` — aí
    // quem abre é o `parseInfoValue` do shell. Cobrir as duas origens evita um
    // modal em branco dependendo de onde o usuário clicou.
    const cru = selectedObject?.movementTypeInfoValue;
    const valores =
      cru && typeof parseInfoValue === "function"
        ? { ...selectedObject, ...parseInfoValue(cru) }
        : selectedObject ?? {};

    setForm({
      subsidiary:
        valores.subsidiary || valores.subsidiaryUuid || "",
      nome:
        valores.nome ||
        valores.movementTypeName ||
        valores.movementTypeInfoDescription ||
        "",
      descricao: valores.descricao || valores.movementTypeDescription || "",
      // A linha antiga podia trazer "Saída"; o CHECK do banco só aceita
      // "saida". Normalizar na leitura evita reenviar o valor recusado.
      categoria: normalizarCategoria(
        valores.categoria || valores.movementCategory || ""
      ),
      // `afetaEstoque` ausente não é `false`: um tipo de movimento que não mexe
      // no saldo é a exceção, não o padrão. Só um `false` explícito desmarca.
      afetaEstoque: (valores.afetaEstoque ?? valores.affectsStock) !== false,
      requerAprovacao:
        (valores.requerAprovacao ?? valores.requiresApproval) === true,
    });
    setAviso(null);
  }, [selectedObject, parseInfoValue]);

  const handleLocalChange = (campo: string, valor: any) => {
    setForm((anterior) => ({ ...anterior, [campo]: valor }));
  };

  const validar = (): string | null => {
    if (!form.subsidiary) return "Escolha a loja do tipo de movimento.";
    if (!form.nome.trim()) return "O nome do tipo de movimento é obrigatório.";
    if (!form.categoria)
      return "Escolha a categoria (Entrada, Saída, Transferência ou Ajuste).";
    return null;
  };

  const handleSubmit = async () => {
    const erro = validar();
    if (erro) {
      setAviso({ tom: "erro", texto: erro });
      return;
    }

    setSalvando(true);
    try {
      const r = await handleSave(tipoDoModal, {
        ...form,
        nome: form.nome.trim(),
        descricao: form.descricao.trim(),
        tipoDeMovimentoUuid: uuidEmEdicao || undefined,
      });
      // Deu certo, o shell já fechou e nada disto pinta. Deu errado, o modal
      // continua aberto — e o motivo aparece aqui, não num alert() nativo.
      if (r && !r.ok && montado.current) {
        setAviso({ tom: r.tom || "erro", texto: r.texto });
      }
    } finally {
      if (montado.current) setSalvando(false);
    }
  };

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo}>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-movementtypemodal">
            <AlfredoStyles.MaterialIcons>
              swap_horiz
            </AlfredoStyles.MaterialIcons>
            {ehEdicao ? "Editar Tipo de Movimento" : "Novo Tipo de Movimento"}
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.FormContainer>
            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Identificação
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Loja *</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={form.subsidiary}
                    onChange={(e) =>
                      handleLocalChange("subsidiary", e.target.value)
                    }
                  >
                    <option value="">Selecione a loja</option>
                    {lojas.map((loja: any) => (
                      <option
                        key={loja.subsidiaryUuid}
                        value={loja.subsidiaryUuid}
                      >
                        {loja.subsidiaryName}
                        {loja.companyName ? ` — ${loja.companyName}` : ""}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Nome *</AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="text"
                    value={form.nome}
                    onChange={(e) =>
                      handleLocalChange("nome", e.target.value)
                    }
                    placeholder="Ex.: Compra, Perda, Transferência entre lojas"
                  />
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer fullWidth>
                  <AlfredoStyles.Label>Descrição</AlfredoStyles.Label>
                  <AlfredoStyles.TextArea
                    value={form.descricao}
                    onChange={(e) =>
                      handleLocalChange("descricao", e.target.value)
                    }
                    placeholder="Quando usar este tipo de movimento"
                  />
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Classificação
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer fullWidth>
                  <AlfredoStyles.Label>
                    Categoria *
                    {/* O selo fica AO LADO do campo, não dentro do <option>:
                        `<option>` só renderiza texto — um <span> ali dentro é
                        descartado pelo navegador. O aviso do item novo vira,
                        então, o sufixo "(novo)" na opção mais este selo. */}
                    <AlfredoStyles.InfoBadge>
                      Ajuste: novo
                    </AlfredoStyles.InfoBadge>
                  </AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={form.categoria}
                    onChange={(e) =>
                      handleLocalChange("categoria", e.target.value)
                    }
                  >
                    <option value="">Selecione a categoria</option>
                    {CATEGORIAS.map((categoria) => (
                      <option key={categoria.valor} value={categoria.valor}>
                        {categoria.rotulo}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>

              {form.categoria === CATEGORIA_NOVA && (
                <AlfredoStyles.AlertMessage $tom="atencao">
                  "Ajuste" é categoria nova: a tela já a prevê, mas os tipos de
                  movimento gravados na rede hoje são só Entrada, Saída e
                  Transferência. Quem depende do legado não vai encontrar esta
                  categoria lá.
                </AlfredoStyles.AlertMessage>
              )}
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Comportamento
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input
                    type="checkbox"
                    checked={form.afetaEstoque}
                    onChange={(e) =>
                      handleLocalChange("afetaEstoque", e.target.checked)
                    }
                  />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>
                  Afeta o estoque
                  <span className="tooltip">
                    Desmarque para movimentos que só registram, sem mexer no
                    saldo
                  </span>
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input
                    type="checkbox"
                    checked={form.requerAprovacao}
                    onChange={(e) =>
                      handleLocalChange("requerAprovacao", e.target.checked)
                    }
                  />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>
                  Requer aprovação
                  <span className="tooltip">
                    O movimento só vale depois de alguém aprovar
                  </span>
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>
            </AlfredoStyles.FormSection>

            {/* Regra da casa: o que a tela não faz é dito na tela. */}
            <AlfredoStyles.AlertMessage $tom="atencao">
              O que você salvar aqui grava em{" "}
              <strong>suprimentos/tipos-de-movimento</strong>, mas a tabela
              acima ainda lê a lista do InfoToken
              (<code>movementTypeInfo</code>), coleção que nenhum backend
              produz. Ou seja: o tipo é criado no banco e{" "}
              <strong>não aparece na lista</strong> até esta tela passar a ler o
              REST paginado. Não é o salvamento que falhou.
            </AlfredoStyles.AlertMessage>

            {aviso && (
              <AlfredoStyles.AlertMessage $tom={aviso.tom}>
                {aviso.texto}
              </AlfredoStyles.AlertMessage>
            )}
          </AlfredoStyles.FormContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton
              onClick={handleSubmit}
              disabled={salvando}
            >
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              {salvando
                ? "Salvando..."
                : ehEdicao
                ? "Atualizar tipo"
                : "Criar tipo"}
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default MovementTypeModal;
